// Tableau and DAX to Sigma formula translation. Pure functions shared by the pages and tests.
// Function names and signatures were checked against help.sigmacomputing.com on 2026-11-03.
// The translator only rewrites what it recognizes; anything else is listed in `unmapped`.

export const KIND_ORDER = ['Direct swap', 'Check behavior', 'Restructure'];

function worse(a, b) {
  return KIND_ORDER.indexOf(b) > KIND_ORDER.indexOf(a) ? b : a;
}

/* ---------- small parsing helpers ---------- */

/** Index of the parenthesis that closes the one at `open`, respecting quotes, brackets and braces. */
function matchParen(text, open) {
  let depth = 0;
  for (let i = open; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"' || ch === "'") {
      const close = text.indexOf(ch, i + 1);
      if (close === -1) return -1;
      i = close;
    } else if (ch === '[') {
      const close = text.indexOf(']', i + 1);
      if (close === -1) return -1;
      i = close;
    } else if (ch === '(') depth += 1;
    else if (ch === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** Split a function's argument text on top-level commas. */
function splitArgs(text) {
  const args = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"' || ch === "'") {
      const close = text.indexOf(ch, i + 1);
      if (close === -1) break;
      i = close;
    } else if (ch === '[') {
      const close = text.indexOf(']', i + 1);
      if (close === -1) break;
      i = close;
    } else if (ch === '(' || ch === '{') depth += 1;
    else if (ch === ')' || ch === '}') depth -= 1;
    else if (ch === ',' && depth === 0) {
      args.push(text.slice(start, i).trim());
      start = i + 1;
    }
  }
  const last = text.slice(start).trim();
  if (last || args.length) args.push(last);
  return args;
}

/**
 * Rewrite every function call in `text`, innermost first.
 * `handler(name, args, ctx)` returns a replacement string, or null to keep the call (args still rewritten).
 */
function rewriteCalls(text, handler, ctx) {
  let out = '';
  let i = 0;
  const callRe = /([A-Za-z_][A-Za-z0-9_.]*)\s*\(/g;
  while (i < text.length) {
    callRe.lastIndex = i;
    const m = callRe.exec(text);
    if (!m) {
      out += text.slice(i);
      break;
    }
    // Skip matches inside quotes or brackets by copying segments up to them.
    const before = text.slice(i, m.index);
    const guard = /["'[]/.exec(before);
    if (guard) {
      const at = i + guard.index;
      const closer = text[at] === '[' ? ']' : text[at];
      const close = text.indexOf(closer, at + 1);
      const end = close === -1 ? text.length : close + 1;
      out += text.slice(i, end);
      i = end;
      continue;
    }
    const open = m.index + m[0].length - 1;
    const close = matchParen(text, open);
    if (close === -1) {
      out += text.slice(i);
      break;
    }
    out += before;
    const name = m[1];
    const rawArgs = splitArgs(text.slice(open + 1, close));
    const replaced = handler(name, rawArgs, ctx);
    if (replaced !== null && replaced !== undefined) out += replaced;
    else out += `${name}(${rawArgs.map((a) => rewriteCalls(a, handler, ctx)).join(', ')})`;
    i = close + 1;
  }
  return out;
}

function addNote(ctx, kind, note) {
  ctx.kind = worse(ctx.kind, kind);
  if (note && !ctx.changes.includes(note)) ctx.changes.push(note);
}

function detect(ctx, label) {
  if (!ctx.detected.includes(label)) ctx.detected.push(label);
}

function finish(ctx, output, defaults) {
  return {
    output: output.trim(),
    kind: ctx.kind,
    detected: ctx.detected,
    unmapped: ctx.unmapped,
    changes: ctx.changes.length ? ctx.changes : [defaults.change],
    where: ctx.where || defaults.where,
    check: ctx.check || defaults.check,
  };
}

function newContext() {
  return { kind: 'Direct swap', detected: [], unmapped: [], changes: [], where: '', check: '' };
}

/* ---------- Tableau ---------- */

const TABLEAU_RENAMES = {
  SUM: 'Sum', AVG: 'Avg', MIN: 'Min', MAX: 'Max', COUNTD: 'CountDistinct', IFNULL: 'Coalesce',
  ISNULL: 'IsNull', IIF: 'If', CONTAINS: 'Contains', LOWER: 'Lower', DATEDIFF: 'DateDiff', DATETRUNC: 'DateTrunc',
};

function tableauStrings(text) {
  // Tableau accepts single-quoted strings; Sigma needs double quotes.
  return text.replace(/'([^'"]*)'/g, '"$1"');
}

/** Turn IF/ELSEIF/ELSE/END and CASE/WHEN blocks into If() and Switch(). Works on one level of nesting at a time. */
function tableauBlocks(text, ctx) {
  const tokenRe = /\b(IF|THEN|ELSEIF|ELSE|END|CASE|WHEN)\b/gi;
  let guard = 0;
  let result = text;
  // Replace innermost blocks first: a block whose body contains no other IF/CASE.
  const innermost = /\b(IF|CASE)\b((?:(?!\b(?:IF|CASE)\b)[\s\S])*?)\bEND\b/i;
  while (innermost.test(result) && guard < 50) {
    guard += 1;
    result = result.replace(innermost, (whole, kind, body) => {
      const parts = [];
      let last = 0;
      let lastKey = kind.toUpperCase();
      tokenRe.lastIndex = 0;
      let t;
      const pieces = [];
      while ((t = tokenRe.exec(body))) {
        pieces.push({ key: lastKey, value: body.slice(last, t.index).trim() });
        lastKey = t[1].toUpperCase();
        last = t.index + t[0].length;
      }
      pieces.push({ key: lastKey, value: body.slice(last).trim() });
      if (kind.toUpperCase() === 'IF') {
        detect(ctx, 'IF');
        for (const p of pieces) if (p.value) parts.push(p.value);
        return `__SIGMA_IF__(${parts.join(', ')})`;
      }
      detect(ctx, 'CASE');
      for (const p of pieces) if (p.value) parts.push(p.value);
      return `__SIGMA_SWITCH__(${parts.join(', ')})`;
    });
  }
  return result.replace(/__SIGMA_IF__\(/g, 'If(').replace(/__SIGMA_SWITCH__\(/g, 'Switch(');
}

function tableauCall(name, args, ctx) {
  const upper = name.toUpperCase();
  const inner = () => args.map((a) => rewriteCalls(a, tableauCall, ctx));
  if (upper === 'ZN') {
    detect(ctx, 'ZN');
    return `Coalesce(${inner()[0] || ''}, 0)`;
  }
  if (upper === 'RUNNING_SUM') {
    detect(ctx, 'RUNNING_SUM');
    addNote(ctx, 'Check behavior', 'Tableau runs along the view’s Compute Using setting. Sigma’s CumulativeSum runs within the table’s grouping and sort order, restarting in each group.');
    ctx.where = ctx.where || 'A column in a grouped table, sorted the way the running total should run.';
    ctx.check = ctx.check || 'The total restarts where you expect, such as each region or year.';
    return `CumulativeSum(${inner()[0] || ''})`;
  }
  if (upper === 'TOTAL' || (upper === 'WINDOW_SUM' && args.length === 1)) {
    detect(ctx, upper);
    addNote(ctx, 'Check behavior', 'TOTAL follows the view’s partition. In Sigma, GrandTotal covers the whole table and Subtotal(..., "parent_grouping") covers the parent group. Pick the level on purpose.');
    ctx.where = ctx.where || 'A column in a grouped table.';
    ctx.check = ctx.check || 'Shares add up to 100% at the level you show, with and without filters applied.';
    return `GrandTotal(${inner()[0] || ''})`;
  }
  if (upper === 'DATEDIFF' || upper === 'DATETRUNC') detect(ctx, upper);
  if (TABLEAU_RENAMES[upper]) {
    detect(ctx, upper);
    return `${TABLEAU_RENAMES[upper]}(${inner().join(', ')})`;
  }
  if (/^(WINDOW_|RUNNING_|RANK|INDEX$|FIRST$|LAST$|LOOKUP$|PREVIOUS_VALUE$|SIZE$)/.test(upper)) {
    detect(ctx, upper);
    addNote(ctx, 'Restructure', `${upper} is a Tableau table calculation. Rebuild it in a grouped Sigma table with the window functions (for example Lag, CumulativeSum, or Rank), then check the partition and sort.`);
    if (!ctx.unmapped.includes(upper)) ctx.unmapped.push(upper);
    return null;
  }
  if (/^[A-Z_]+$/.test(name) && !ctx.unmapped.includes(upper)) {
    ctx.unmapped.push(upper);
    addNote(ctx, 'Check behavior', `${upper} isn’t in this translator’s map. Look it up in Sigma’s function list before you rely on the output.`);
  }
  return null;
}

export function translateTableau(input) {
  const ctx = newContext();
  let text = String(input || '').replace(/\/\/.*$/gm, '').trim();
  if (!text) return finish(ctx, '', { change: 'Paste a calculated field to translate it.', where: '', check: '' });

  // Level of detail expressions.
  const lod = /^\{\s*(FIXED|INCLUDE|EXCLUDE)\s*([^:]*):\s*([\s\S]+)\}$/i.exec(text);
  if (lod) {
    const type = lod[1].toUpperCase();
    const dims = lod[2].replace(/[[\]]/g, '').trim();
    detect(ctx, type);
    text = lod[3].trim();
    addNote(ctx, 'Restructure', 'Sigma has no LOD keywords. The level of detail comes from the grouping a column sits in.');
    if (type === 'FIXED') {
      addNote(ctx, 'Restructure', 'FIXED ignores dimension filters in Tableau. A Sigma grouping respects workbook filters, so filter-sensitive results can change.');
      ctx.where = dims
        ? `A column at a ${dims} grouping level. To use it on every row, build a table grouped by ${dims} and bring it back with Lookup().`
        : 'A GrandTotal() column: a FIXED expression with no dimensions is a table-wide total.';
    } else if (type === 'INCLUDE') {
      ctx.where = `Group by ${dims || 'the extra dimension'} below your current grouping, calculate there, then aggregate up a level.`;
    } else {
      ctx.where = `Calculate at a grouping that leaves out ${dims || 'the excluded dimension'}, often a parent grouping with Subtotal(..., "parent_grouping").`;
    }
    ctx.check = 'Compare the result for a few rows with filters on and off.';
  }

  text = tableauStrings(text);
  text = tableauBlocks(text, ctx);
  let output = rewriteCalls(text, tableauCall, ctx);
  if (/\bELSEIF\b|\bTHEN\b|\bWHEN\b/i.test(output)) {
    addNote(ctx, 'Check behavior', 'Part of an IF or CASE block could not be converted. Check the nesting.');
  }
  output = output.replace(/==/g, '=');
  if (ctx.detected.includes('IF') && ctx.kind === 'Direct swap') {
    ctx.changes.push('IF THEN ELSEIF ELSE END becomes one If() with condition and value pairs, and the else value last.');
  }
  if (ctx.detected.includes('CONTAINS')) {
    ctx.changes.push('Contains is case sensitive in Sigma. Wrap both sides in Lower() to ignore case.');
  }
  return finish(ctx, output, {
    change: 'Same logic, Sigma function names. Strings use double quotes.',
    where: 'A row-level column or a column in a grouped table, wherever the Tableau field was used.',
    check: 'Compare a few rows against the Tableau result on the same data.',
  });
}

/* ---------- DAX ---------- */

const DAX_RENAMES = {
  SUM: 'Sum', AVERAGE: 'Avg', MIN: 'Min', MAX: 'Max', DISTINCTCOUNT: 'CountDistinct', COALESCE: 'Coalesce', IF: 'If', ISBLANK: 'IsNull',
};

const DAX_IF_VERSION = { SUM: 'SumIf', AVERAGE: 'AvgIf', MIN: 'MinIf', MAX: 'MaxIf', DISTINCTCOUNT: 'CountDistinctIf' };

function daxColumns(text) {
  // 'Table Name'[Column] or Table[Column] -> [Column]
  return text.replace(/'[^']+'\s*(\[[^\]]+\])/g, '$1').replace(/\b[A-Za-z_][A-Za-z0-9_]*\s*(\[[^\]]+\])/g, '$1');
}

function daxOperators(text) {
  return text.replace(/&&/g, ' and ').replace(/\|\|/g, ' or ').replace(/\s{2,}/g, ' ');
}

function isFilterRemover(arg) {
  return /^\s*(ALL|ALLEXCEPT|ALLSELECTED|REMOVEFILTERS)\s*\(/i.test(arg);
}

function timeShift(arg) {
  if (/^\s*SAMEPERIODLASTYEAR\s*\(/i.test(arg)) return { amount: 1, period: 'year', label: 'SAMEPERIODLASTYEAR' };
  const m = /^\s*(DATEADD|PARALLELPERIOD)\s*\([^,]+,\s*(-?\d+)\s*,\s*(DAY|MONTH|QUARTER|YEAR)\s*\)/i.exec(arg);
  if (m) return { amount: -Number(m[2]), period: m[3].toLowerCase(), label: m[1].toUpperCase() };
  return null;
}

function daxCall(name, args, ctx) {
  const upper = name.toUpperCase();
  const inner = () => args.map((a) => rewriteCalls(a, daxCall, ctx));

  if (upper === 'BLANK' && args.length <= 1 && !args[0]) {
    detect(ctx, 'BLANK');
    return 'Null';
  }
  if (upper === 'DIVIDE') {
    detect(ctx, 'DIVIDE');
    const [num, den, alt] = inner();
    addNote(ctx, 'Check behavior', 'DIVIDE returns blank (or your alternate value) when the denominator is zero. The Sigma version writes that case out instead of relying on default division behavior.');
    ctx.check = ctx.check || 'Groups with a zero denominator show blank, not an error or zero.';
    return `If(${den} = 0, ${alt || 'Null'}, ${num} / ${den})`;
  }
  if (/^(SUMX|AVERAGEX|MINX|MAXX)$/.test(upper)) {
    detect(ctx, upper);
    const target = { SUMX: 'Sum', AVERAGEX: 'Avg', MINX: 'Min', MAXX: 'Max' }[upper];
    const expr = args.length > 1 ? rewriteCalls(args.slice(1).join(', '), daxCall, ctx) : '';
    if (/^\s*FILTER\s*\(/i.test(args[0] || '')) {
      addNote(ctx, 'Restructure', 'The FILTER table argument becomes a condition. Use the If version of the aggregate (SumIf, AvgIf) or filter the table.');
    }
    ctx.changes.push(`${upper} iterates rows; in Sigma the row expression goes straight inside ${target}(). For readability, add it as a row-level column first.`);
    return `${target}(${expr})`;
  }
  if (upper === 'RELATED') {
    detect(ctx, 'RELATED');
    const col = (args[0] || '').trim();
    const table = (/^'?([^'[]+)'?\s*\[/.exec(col) || [])[1] || 'Other Table';
    const column = (/\[([^\]]+)\]/.exec(col) || [])[1] || 'Column';
    addNote(ctx, 'Restructure', 'Power BI follows the model relationship. In Sigma, name the keys in Lookup(), or join the tables once in a data model and reuse it.');
    ctx.where = ctx.where || 'A row-level column on the fact table, or a join in a Sigma data model.';
    ctx.check = ctx.check || 'Every row finds a match. Count the rows where the result is null.';
    return `Lookup([${table.trim()}/${column}], [Key], [${table.trim()}/Key])`;
  }
  if (upper === 'CALCULATE') {
    detect(ctx, 'CALCULATE');
    const [expr, ...filters] = args;
    const removers = filters.filter(isFilterRemover);
    const shifts = filters.map(timeShift).filter(Boolean);
    const conditions = filters.filter((f) => !isFilterRemover(f) && !timeShift(f));
    removers.forEach((r) => detect(ctx, r.trim().split('(')[0].toUpperCase()));
    shifts.forEach((s) => detect(ctx, s.label));
    const aggMatch = /^\s*(SUM|AVERAGE|MIN|MAX|DISTINCTCOUNT|COUNTROWS)\s*\(([\s\S]*)\)\s*$/i.exec(expr || '');
    const rewriteBase = () => rewriteCalls(expr || '', daxCall, ctx);

    if (shifts.length) {
      const base = rewriteBase();
      const s = shifts[0];
      addNote(ctx, 'Restructure', 'Time intelligence relies on a marked date table in Power BI. In Sigma, group by the date period and use DateLookback(), which returns Null when the earlier period has no data.');
      ctx.where = 'A column in a table grouped by month (or the period you report on), next to the current value.';
      ctx.check = ctx.check || 'Last year’s value for a few months matches the Power BI measure, including the first month of the year.';
      return `DateLookback(${base}, [Month of Date], ${s.amount}, "${s.period}")`;
    }
    if (conditions.length && aggMatch) {
      const fn = aggMatch[1].toUpperCase();
      detect(ctx, fn);
      const conds = conditions.map((c) => daxOperators(rewriteCalls(c, daxCall, ctx)));
      addNote(ctx, 'Restructure', 'CALCULATE changes filter context. In Sigma the condition moves into the aggregate itself.');
      ctx.where = ctx.where || 'A metric or a column in a grouped table.';
      ctx.check = ctx.check || 'The result matches with no other filters, then with a date filter applied.';
      if (fn === 'COUNTROWS') return `CountIf(${conds.join(', ')})`;
      const column = rewriteCalls(aggMatch[2], daxCall, ctx);
      if (fn === 'AVERAGE' && conds.length > 1) return `AvgIf(${column}, ${conds.join(' and ')})`;
      return `${DAX_IF_VERSION[fn]}(${column}, ${conds.join(', ')})`;
    }
    if (conditions.length) {
      const conds = conditions.map((c) => daxOperators(rewriteCalls(c, daxCall, ctx)));
      addNote(ctx, 'Restructure', `CALCULATE wraps the measure ${(expr || '').trim()}, so the condition moves into that measure’s aggregate. Open its definition, swap Sum for SumIf (or Avg for AvgIf), and replace [Column] with the column it aggregates.`);
      ctx.where = ctx.where || 'A metric or a column in a grouped table.';
      ctx.check = ctx.check || 'The result matches with no other filters, then with a date filter applied.';
      return `SumIf([Column], ${conds.join(', ')})`;
    }
    const base = rewriteBase();
    if (removers.length) {
      addNote(ctx, 'Check behavior', 'ALL and REMOVEFILTERS widen the filter context. In Sigma, GrandTotal() covers the whole table and Subtotal(..., "parent_grouping") the parent group. Test whether filtered-out rows should count.');
      ctx.where = ctx.where || 'A column in a grouped table.';
      ctx.check = ctx.check || 'Shares add up to 100% with and without a filter on the removed column.';
      if (!conditions.length) return `GrandTotal(${base})`;
    }
    return base;
  }
  if (/^(TOTALYTD|TOTALQTD|TOTALMTD)$/.test(upper)) {
    detect(ctx, upper);
    const period = { TOTALYTD: 'year', TOTALQTD: 'quarter', TOTALMTD: 'month' }[upper];
    addNote(ctx, 'Restructure', `Time intelligence relies on a marked date table. In Sigma, group by ${period} so the running total restarts each ${period}, then sort by date.`);
    ctx.where = `A column in a table grouped by ${period}, then by the date period you show, sorted by date.`;
    ctx.check = ctx.check || `The total restarts in the first period of each ${period}, including fiscal calendars.`;
    return `CumulativeSum(${rewriteCalls(args[0] || '', daxCall, ctx)})`;
  }
  if (DAX_RENAMES[upper]) {
    detect(ctx, upper);
    return `${DAX_RENAMES[upper]}(${inner().join(', ')})`;
  }
  if (/^(FILTER|VALUES|SELECTEDVALUE|HASONEVALUE|SUMMARIZE|ADDCOLUMNS|USERELATIONSHIP|CROSSFILTER|EARLIER|RANKX|TOPN)$/.test(upper)) {
    detect(ctx, upper);
    addNote(ctx, 'Restructure', `${upper} works on filter context or tables, which Sigma expresses with groupings, controls, and joins rather than a single formula.`);
    if (!ctx.unmapped.includes(upper)) ctx.unmapped.push(upper);
    return null;
  }
  if (/^[A-Z_]+$/.test(name) && !ctx.unmapped.includes(upper)) {
    ctx.unmapped.push(upper);
    addNote(ctx, 'Check behavior', `${upper} isn’t in this translator’s map. Look it up in Sigma’s function list before you rely on the output.`);
  }
  return null;
}

export function translateDax(input) {
  const ctx = newContext();
  let text = String(input || '').replace(/\/\/.*$/gm, '').replace(/--.*$/gm, '').trim();
  if (!text) return finish(ctx, '', { change: 'Paste a measure to translate it.', where: '', check: '' });
  const named = /^\s*([^=()[\]"]+?)\s*:?=\s*(?!=)([\s\S]+)$/.exec(text);
  if (named && !/[<>!]$/.test(named[1])) text = named[2].trim();
  // Rewrite calls before stripping table names so RELATED and CALCULATE still see them.
  let output = rewriteCalls(text, daxCall, ctx);
  output = daxOperators(daxColumns(output));
  return finish(ctx, output, {
    change: 'Same logic, Sigma function names. Column references drop the table name.',
    where: 'A metric, or a column in a grouped table where the measure was used.',
    check: 'Compare the total and a few groups against the Power BI result on the same data.',
  });
}

/* ---------- examples and function maps shown on the pages ---------- */

export const TABLEAU_EXAMPLES = [
  { label: 'FIXED LOD', input: '{FIXED [Customer] : MIN([Order Date])}' },
  { label: 'ZN null to zero', input: 'ZN(SUM([Sales]))' },
  { label: 'DATEDIFF', input: "DATEDIFF('day', [Order Date], [Ship Date])" },
  { label: 'IF ELSEIF', input: 'IF [Sales] > 1000 THEN "Large" ELSEIF [Sales] > 100 THEN "Medium" ELSE "Small" END' },
  { label: 'RUNNING_SUM', input: 'RUNNING_SUM(SUM([Sales]))' },
  { label: 'Percent of total', input: 'SUM([Sales]) / TOTAL(SUM([Sales]))' },
];

export const DAX_EXAMPLES = [
  { label: 'CALCULATE filter', input: 'East Revenue = CALCULATE(SUM(Sales[Amount]), Sales[Region] = "East")' },
  { label: 'DIVIDE', input: 'AOV = DIVIDE([Revenue], [Orders])' },
  { label: 'SUMX row math', input: 'Gross Margin = SUMX(Sales, Sales[Amount] - Sales[Cost])' },
  { label: '% of total with ALL', input: 'Share = DIVIDE(SUM(Sales[Amount]), CALCULATE(SUM(Sales[Amount]), ALL(Sales[Region])))' },
  { label: 'RELATED lookup', input: 'Segment = RELATED(Customers[Segment])' },
  { label: 'Same period last year', input: 'Revenue LY = CALCULATE(SUM(Sales[Amount]), SAMEPERIODLASTYEAR(\'Date\'[Date]))' },
  { label: 'TOTALYTD', input: "Revenue YTD = TOTALYTD(SUM(Sales[Amount]), 'Date'[Date])" },
];

export const TABLEAU_MAP = [
  { from: 'SUM / AVG / MIN / MAX', to: 'Sum / Avg / Min / Max', kind: 'Direct swap', note: 'Same meaning.' },
  { from: 'COUNTD([x])', to: 'CountDistinct([x])', kind: 'Direct swap', note: 'Skips nulls.' },
  { from: 'IFNULL / ZN', to: 'Coalesce([x], 0)', kind: 'Direct swap', note: 'One function covers both.' },
  { from: 'DATEDIFF / DATETRUNC', to: 'DateDiff / DateTrunc', kind: 'Direct swap', note: 'Unit first, as a lowercase string in double quotes.' },
  { from: 'IIF / IF THEN ELSEIF END', to: 'If(cond, value, ..., else)', kind: 'Direct swap', note: 'One If() takes several condition and value pairs.' },
  { from: 'CASE WHEN END', to: 'Switch(value, case, result, ..., else)', kind: 'Direct swap', note: 'Same order of arguments.' },
  { from: 'CONTAINS', to: 'Contains', kind: 'Check behavior', note: 'Case sensitive. Wrap in Lower() to ignore case.' },
  { from: 'RUNNING_SUM', to: 'CumulativeSum', kind: 'Check behavior', note: 'Restarts in each group; follows sort order.' },
  { from: 'TOTAL / WINDOW_SUM', to: 'GrandTotal / Subtotal', kind: 'Check behavior', note: 'Pick the level explicitly.' },
  { from: '{FIXED} / {INCLUDE} / {EXCLUDE}', to: 'Grouping levels or Lookup', kind: 'Restructure', note: 'Filters behave differently.' },
];

export const DAX_MAP = [
  { from: 'SUM / AVERAGE / MIN / MAX', to: 'Sum / Avg / Min / Max', kind: 'Direct swap', note: 'Same meaning.' },
  { from: 'DISTINCTCOUNT', to: 'CountDistinct', kind: 'Direct swap', note: 'Skips nulls.' },
  { from: 'COALESCE / BLANK()', to: 'Coalesce / Null', kind: 'Direct swap', note: 'Same meaning.' },
  { from: 'SUMX / AVERAGEX', to: 'Sum(expr) / Avg(expr)', kind: 'Direct swap', note: 'Row math goes inside the aggregate.' },
  { from: 'DIVIDE', to: 'If(d = 0, Null, n / d)', kind: 'Check behavior', note: 'Write the zero case out.' },
  { from: 'CALCULATE + filter', to: 'SumIf / CountIf / AvgIf', kind: 'Restructure', note: 'The condition moves into the aggregate.' },
  { from: 'ALL / REMOVEFILTERS', to: 'GrandTotal / Subtotal', kind: 'Check behavior', note: 'Choose the level explicitly.' },
  { from: 'SAMEPERIODLASTYEAR / DATEADD', to: 'DateLookback', kind: 'Restructure', note: 'Group by the date period first.' },
  { from: 'TOTALYTD', to: 'CumulativeSum in a year grouping', kind: 'Restructure', note: 'The grouping resets the total.' },
  { from: 'RELATED', to: 'Lookup', kind: 'Restructure', note: 'Name the keys, or join in a data model.' },
];
